<script lang="ts">
  // UI components
  import { Button } from '$lib/components/ui/button';
  import * as Card from '$lib/components/ui/card/index';
  import { Input } from '$lib/components/ui/input/index';
  import { Separator } from '$lib/components/ui/separator/index';
  import * as Table from '$lib/components/ui/table/index';
  import { MapPinPlus } from '@lucide/svelte';
  // RoastLog
  import RoastEventButton from '$lib/components/roast/RoastEventButton.svelte';
  import RoastingEventEditDialog from '$lib/components/roast/RoastingEventEdit.svelte';
  import RoastNotes from '$lib/components/roast/RoastNotes.svelte';
  import logger from '$lib/logger';
  import { selectedRoaster, userProfile } from '$lib/PersistedState.svelte';
  import { formatTime } from '$lib/RLogUtils';
  import { addEvent, deleteEvent } from '$lib/roasting/roastRecorder.svelte';
  import { roastingEvents } from '$lib/RoastingEvents.svelte';
  import { globalState, newRoastEvents } from '$lib/State.svelte';
  import type { NewRoastEvent, Nullable, RoastingEvent } from '$lib/types/RoastLog';

  let editEventDialogOpen: boolean = $state(false);
  let eventToEdit: Nullable<NewRoastEvent> = $state(null);
  let adHocEvent: string = $state('');

  const roastEvents = $derived(newRoastEvents.current.toSorted((a, b) => b.time - a.time));

  const startSecondaryTimer = () => {
    globalState.startSecondaryTimer();
    addEvent({
      time: globalState.elapsedSeconds!,
      temperature: globalState.readings.beanTemp!,
      description: 'Start first crack',
      isFirstCrack: true
    });
  };

  const eventTriggered = (e: any) => {
    const { event, value }: { event: RoastingEvent; value: number[] | undefined } = e;

    let description = event.name;
    if (value) {
      const eventValue = value[0];
      description = `${description} to ${eventValue}`;
    }

    // TODO - this should be managed in the ActiveRoast item in the global state.
    addEvent({
      time: globalState.elapsedSeconds!,
      temperature: globalState.readings.beanTemp!,
      description: description,
      isFirstCrack: false
    });
  };

  const adHocEvenTriggered = (e: any) => {
    logger.info('Ad-hoc event triggered', { description: adHocEvent });
    addEvent({
      time: globalState.elapsedSeconds!,
      temperature: globalState.readings.beanTemp!,
      description: adHocEvent,
      isFirstCrack: false
    });
    adHocEvent = '';
  };

  const onEventUpdated = (time: number) => {
    const updatedTemp = globalState.getBeanTempAtTime(time);
    if (updatedTemp && eventToEdit) {
      eventToEdit.temperature = updatedTemp.toFixed(1);
    } else {
      logger.warn(`Could not find temperature at time ${time}`);
    }
  };

  const onEventDeleted = () => {
    if (!eventToEdit) {
      logger.warn(`Could not find event to delete`);
      return;
    }
    deleteEvent(eventToEdit);
  };
</script>

<!--
Edit dialog for roasting events. The component manages updating the global state of roasting
events.
 -->
<RoastingEventEditDialog
  bind:open={editEventDialogOpen}
  roastEvent={eventToEdit!}
  maxSeconds={globalState.elapsedSeconds!}
  {onEventUpdated}
  {onEventDeleted}
/>

<div class="flex h-[calc(100vh-128px)] flex-col">
  <!-- First part with bean temp and other metrics. 75%-->
  <Card.Root>
    <Card.Content class="grid grid-cols-2 gap-5 p-5 text-xl">
      <!-- row 1-->
      <div class="text-6xl">{globalState.readings.timer}</div>
      <div class="text-6xl">
        {#if !globalState.readings.beanTemp}
          -
        {:else}
          <div class="flex items-end justify-between">
            <div class="w-4/5">
              {globalState.readings.beanTemp}
            </div>
            <div class="text-4xl">
              °{userProfile.current?.profile.temperature_scale.toUpperCase()}
            </div>
          </div>
        {/if}
      </div>

      <!-- row 2-->
      <div>
        {#if selectedRoaster.tcLabels.tc2}
          {selectedRoaster.tcLabels.tc2}: {globalState.readings.tc2Temp || '-'}
        {/if}
      </div>
      <div>
        Heating Rate: {globalState.readings.heatingRate.converted
          ? globalState.readings.heatingRate.converted.toFixed(1)
          : '-'}
      </div>

      <!-- row 3-->
      <div>
        {#if selectedRoaster.tcLabels.tc3}
          {selectedRoaster.tcLabels.tc3}: {globalState.readings.tc3Temp || '-'}
        {/if}
      </div>
      <div>
        {#if selectedRoaster.tcLabels.tc4}
          {selectedRoaster.tcLabels.tc4}: {globalState.readings.tc4Temp || '-'}
        {/if}
      </div>

      {#if globalState.readings.secondaryTimer}
        <div class="col-span-2">
          <Separator />
        </div>
        <div class="flex flex-col text-2xl">
          <span class="text-sm">Secondary timer:</span>
          {globalState.readings.secondaryTimer}
        </div>
        <div class="flex flex-col text-2xl">
          <span class="text-sm">Development ratio:</span>
          {globalState.readings.developmentRatio} %
        </div>
      {/if}

      {#if globalState.readings.burner}
        <div>
          <div class="text-2xl">Burner: {globalState.readings.burner}%</div>
        </div>
      {/if}
    </Card.Content>
  </Card.Root>

  <Card.Root class="mt-5 overflow-y-auto">
    <Card.Content class="pb-0">
      <RoastNotes disabled={!globalState.isRecording} />
    </Card.Content>
  </Card.Root>

  {#if roastEvents.length > 0}
    <Card.Root class="mt-5 overflow-y-auto">
      <Card.Content class="text-l grid grid-cols-1 gap-5 p-2">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.Head colspan={3} class="text-card-foreground text-center text-lg">
                {#if globalState.isRecording}
                  Current roast events
                {:else}
                  Previous roast events
                {/if}
              </Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {#each roastEvents as e, i}
              <Table.Row
                onclick={() => {
                  if (globalState.isRecording) {
                    editEventDialogOpen = true;
                    eventToEdit = roastEvents[i];
                  }
                }}
              >
                <Table.Cell class="w-1/4 font-medium">{formatTime(e.time)}</Table.Cell>
                <Table.Cell>{e.description}</Table.Cell>
                <Table.Cell class="w-1/4 text-right">{e.temperature}°</Table.Cell>
              </Table.Row>
            {/each}
          </Table.Body>
        </Table.Root>
      </Card.Content>
    </Card.Root>
  {/if}

  <!-- Event buttons, aligned to the bottom-->
  <div class="col-span-2 mt-auto flex flex-col gap-y-5 py-5">
    <Button
      onclick={startSecondaryTimer}
      disabled={!globalState.isRecording || globalState.firstCrackStarted === true}
    >
      First crack start
    </Button>

    <div class="flex gap-2">
      <Input
        class="bg-primary-foreground dark:bg-primary dark:text-primary-foreground flex-1"
        disabled={!globalState.isRecording}
        maxlength={48}
        bind:value={adHocEvent}
        onkeydown={(e) => {
          if (adHocEvent !== '' && e.key === 'Enter') {
            logger.info('Ad-hoc event trigger on Enter');
            adHocEvenTriggered(e);
          }
        }}
      />
      <Button
        size="icon"
        variant="outline"
        class="text-primary hover:border-primary dark:border-white"
        disabled={!globalState.isRecording || adHocEvent === ''}
        onclick={adHocEvenTriggered}
      >
        <MapPinPlus />
      </Button>
    </div>
  </div>

  <div class="grid grid-cols-2 gap-5">
    {#each roastingEvents.items as event, i}
      <RoastEventButton {event} disabled={!globalState.isRecording} {eventTriggered} />
    {/each}
  </div>
</div>
